import React, { useState, useEffect } from 'react';
import { History, Search, Download, Clock, Activity, Trash2, Edit, RefreshCw, User, FileText, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { API_URL } from '../config';

const HistoryPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}?action=get_history`);
      if (Array.isArray(response.data)) setRecords(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching history:", error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === '0000-00-00 00:00:00') return '-';
    try {
      const datePart = dateString.split(' ')[0];
      const [year, month, day] = datePart.split('-');
      if (year && month && day) return `${month}/${day}/${year}`;
      return dateString;
    } catch {
      return dateString;
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric', 
        hour: 'numeric', minute: '2-digit', hour12: true 
      });
    } catch {
      return ts;
    }
  };

  const filteredRecords = records.filter(item => 
    (item.patient_name && item.patient_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.drug_name && item.drug_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.rx_no && item.rx_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.action_type && item.action_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.agent && item.agent.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalEntries = filteredRecords.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  const handleDownloadExcel = () => {
    const exportData = records.map(r => ({
      'Date/Time': formatTimestamp(r.timestamp),
      'Action': r.action_type,
      'Patient': r.patient_name,
      'RX No': r.rx_no,
      'Drug Name': r.drug_name,
      'Qty': r.quantity,
      'Expiration': formatDate(r.expiration_date),
      'Status': r.status,
      'Details': r.details,
      'Agent': r.agent
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, "Activity_History");
    XLSX.writeFile(wb, "Prescription_Activity_History.xlsx");
  };

  const getActionBadge = (action) => {
    const act = (action || '').toLowerCase();
    if (act.includes('delete')) {
      return <span className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1"><Trash2 size={10}/> Deleted</span>;
    }
    if (act.includes('status')) {
      return <span className="bg-purple-100 text-purple-700 border border-purple-200 text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1"><RefreshCw size={10}/> Status Update</span>;
    }
    if (act.includes('deliver')) {
      return <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1"><Check size={10}/> Delivered</span>;
    }
    return <span className="bg-blue-100 text-blue-700 border border-blue-200 text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1"><Edit size={10}/> Updated</span>;
  };

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 animate-fade-in">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-extrabold text-gray-800 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                <History size={22} strokeWidth={2.5} />
              </div>
              Activity History
            </h2>
            <p className="text-gray-500 text-sm mt-1 ml-14">
              Audit log of all prescription updates, edits, and deletions.
            </p>
          </div>

          <div className="flex items-center gap-3 self-end md:self-auto">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400 group-focus-within:text-teal-500 transition-colors">
                <Search size={16} />
              </div>
              <input 
                type="text" 
                placeholder="Search patient, drug, agent..." 
                className="pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white w-full md:w-72 transition-all shadow-sm text-sm" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button 
              onClick={handleDownloadExcel} 
              className="bg-teal-600 text-white px-4 py-2.5 rounded-xl hover:bg-teal-700 flex items-center gap-2 shadow-sm shadow-teal-600/20 font-bold text-sm transition-all active:scale-95"
            >
              <Download size={16} /> Export
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1 custom-scrollbar">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-wider whitespace-nowrap">Timestamp</th>
                <th className="px-5 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-wider">Action</th>
                <th className="px-5 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-wider">Patient & RX</th>
                <th className="px-5 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-wider">Drug Details</th>
                <th className="px-5 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-wider">Status</th>
                <th className="px-5 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-wider">Details</th>
                <th className="px-5 py-4 text-left font-bold text-gray-500 text-[10px] uppercase tracking-wider">Agent</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="7" className="p-12 text-center">
                    <div className="flex flex-col items-center justify-center text-teal-600">
                      <RefreshCw size={24} className="animate-spin mb-3" />
                      <span className="font-bold text-sm">Loading activity logs...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedRecords.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-16 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <Activity size={32} className="mb-3 text-gray-300" />
                      <span className="font-bold text-gray-500">No activity history found.</span>
                      <p className="text-xs mt-1">Prescription edits and deletions will appear here.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRecords.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                        <Clock size={12} className="text-gray-400" />
                        {formatTimestamp(item.timestamp)}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {getActionBadge(item.action_type)}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-800 text-sm truncate max-w-[200px]" title={item.patient_name}>
                          {item.patient_name || <span className="text-gray-400 italic">Unknown</span>}
                        </span>
                        <span className="text-[10px] text-gray-500 font-mono mt-0.5">RX: {item.rx_no || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col">
                        <span className="font-bold text-teal-700 text-xs truncate max-w-[200px]" title={item.drug_name}>
                          {item.drug_name || '-'}
                        </span>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-1 font-mono">
                          <span>Qty: <b className="text-gray-700">{item.quantity}</b></span>
                          <span>Exp: <b className="text-gray-700">{formatDate(item.expiration_date)}</b></span>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {item.status ? (
                        <span className="bg-gray-100 text-gray-700 border border-gray-200 text-[10px] font-bold px-2 py-0.5 rounded-full inline-block truncate max-w-[120px]" title={item.status}>
                          {item.status}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-xs text-gray-600 truncate max-w-[250px]" title={item.details}>
                        {item.details || '-'}
                      </p>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 font-medium">
                        <div className="w-5 h-5 rounded bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                          <User size={10} />
                        </div>
                        {item.agent || 'System'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        {!loading && filteredRecords.length > 0 && (
          <div className="bg-gray-50/80 border-t border-gray-100 px-5 py-3 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 font-medium gap-4">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white cursor-pointer"
              >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value="all">All</option>
              </select>
              <span>entries</span>
            </div>
            
            <span>Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} records</span>
            
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || entriesPerPage === 'all'}
                className="p-1 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50 transition"
                title="Previous Page"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="px-3 py-1 border border-gray-200 rounded bg-white font-semibold shadow-sm">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || entriesPerPage === 'all'}
                className="p-1 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50 transition"
                title="Next Page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;